class CartDrawer extends HTMLElement {
  
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
      fetch('/?sections=cart-drawer')
        .then(r => r.json())
        .then(sections => {
          const html = sections['cart-drawer'];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cartDrawerEl = document.getElementById('CartDrawer');
          const src = doc.querySelector('#CartDrawer');
          if (cartDrawerEl && src) {
            // This fetch resolves AFTER the drawer has already opened, so the
            // user may have started scrolling (e.g. down to the recommended
            // products at the bottom of cart-drawer-items). Replacing innerHTML
            // recreates that scroller at scrollTop 0 and yanks them back up to
            // the cart items. Preserve the scroll position across the swap.
            const prevScroller = cartDrawerEl.querySelector('cart-drawer-items');
            const savedScrollTop = prevScroller ? prevScroller.scrollTop : 0;
            cartDrawerEl.innerHTML = src.innerHTML;
            const newScroller = cartDrawerEl.querySelector('cart-drawer-items');
            if (newScroller && savedScrollTop) newScroller.scrollTop = savedScrollTop;
            const overlay = document.getElementById('CartDrawer-Overlay');
            if (overlay) overlay.addEventListener('click', this.close.bind(this));
          }
          const drawerEl = document.querySelector('cart-drawer');
          const srcDrawer = doc.querySelector('cart-drawer');
          if (drawerEl && srcDrawer) {
            drawerEl.classList.toggle('is-empty', srcDrawer.classList.contains('is-empty'));
          }
        })
        .catch(() => {});
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    this.lockScroll();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  // Body-level `overflow: hidden` (the .overflow-hidden class) doesn't reliably
  // stop the page behind from scrolling — notably iOS momentum touch-scroll. Pin
  // the body with position:fixed instead, remembering the scroll offset so it can
  // be restored on close. Keep the class too (other CSS hooks key off it) and
  // compensate for the removed scrollbar so desktop content doesn't shift.
  lockScroll() {
    if (document.body.hasAttribute('data-scroll-locked')) return;
    const scrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.setAttribute('data-scroll-locked', scrollY);
    document.body.classList.add('overflow-hidden');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  unlockScroll() {
    if (!document.body.hasAttribute('data-scroll-locked')) return;
    const scrollY = parseInt(document.body.getAttribute('data-scroll-locked'), 10) || 0;
    document.body.removeAttribute('data-scroll-locked');
    document.body.classList.remove('overflow-hidden');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, scrollY);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
